// ─── Politique de la file de synchronisation ────────────────────────────
//
// Créé le 2026-08-01 après le diagnostic de la file bloquée.
//
// CE QU'ON A TROUVÉ. 30 consultations empilées le 2026-07-24 en 129 ms,
// rattachées à 6 clientes qui n'existent NI en base, NI en local, NI dans le
// registre des suppressions. Chaque démarrage du cockpit rejouait les 30,
// en séquentiel, et les 30 échouaient sur
// `nutrition_consultations_client_id_fkey`. La file ne se vidait jamais et
// noyait les vraies erreurs — 29 erreurs affichées dans la console, presque
// toutes venant de là.
//
// POURQUOI ÇA NE POUVAIT PAS S'ARRÊTER. `retrySyncQueue` faisait
// `if (error) remaining.push(op)` : une violation de clé étrangère,
// définitive par nature, était remise en file exactement comme une coupure
// réseau. Aucun compteur, aucune classification.
//
// CE QUE CE MODULE APPORTE, selon la consigne d'Anissa :
//  - distinguer un échec DÉFINITIF d'un échec passager ;
//  - mettre en quarantaine plutôt que supprimer — jamais de perte silencieuse ;
//  - laisser les autres éléments se synchroniser ;
//  - permettre la récupération quand la cause est corrigée.

export const DECISION_ECHEC = Object.freeze({
  REJOUER: 'rejouer',
  QUARANTAINE: 'quarantaine',
});

/** Au-delà, on cesse de rejouer même sans code d'erreur reconnu. */
export const MAX_TENTATIVES = 5;

/**
 * Codes PostgreSQL qu'aucun rejeu ne résoudra. Les rejouer indéfiniment ne
 * fait que consommer du réseau et masquer les vraies pannes.
 */
const CODES_DEFINITIFS = Object.freeze({
  23505: 'doublon sur une contrainte d\'unicité',
  23514: 'contrainte de validation non respectée',
  23502: 'colonne obligatoire absente',
  '22P02': 'format de valeur invalide',
  42703: 'colonne inconnue — migration manquante',
  '42P01': 'table inconnue — migration manquante',
  42501: 'droits insuffisants (RLS)',
});

/**
 * Code de clé étrangère. Volontairement ABSENT de CODES_DEFINITIFS.
 *
 * Correction du 2026-08-01 (Anissa) : classer tout 23503 comme définitif est
 * faux. Une clé étrangère peut manquer TEMPORAIREMENT parce que la cliente
 * n'a pas encore été synchronisée — sa propre opération est peut-être encore
 * dans la file, derrière celle de la consultation. Condamner immédiatement
 * reviendrait à perdre une donnée parfaitement légitime.
 *
 * Le caractère définitif ne peut venir que d'un TOMBSTONE identifié : la
 * cliente a été purgée volontairement, elle ne reviendra pas.
 */
const CODE_CLE_ETRANGERE = '23503';

/**
 * Refus de recréation d'une entité définitivement supprimée, levé par le
 * déclencheur `refuser_recreation_supprimee` (V97.157).
 *
 * On vérifie le code ET le motif : `P0002` est un SQLSTATE générique que
 * n'importe quelle autre exception personnalisée pourrait porter. Sans cette
 * double vérification, on condamnerait des données pour une exception qui
 * n'a rien à voir.
 */
const CODE_RECREATION_REFUSEE = 'P0002';
const MOTIF_RECREATION_REFUSEE = /d[ée]finitivement supprim/i;

function estRecreationRefusee(erreur) {
  return codeErreur(erreur) === CODE_RECREATION_REFUSEE
    && MOTIF_RECREATION_REFUSEE.test(String((erreur && erreur.message) || ''));
}

/**
 * Refus visant une cliente en CORBEILLE, levé par
 * `refuser_generation_cliente_supprimee` (V97.180).
 *
 * Il porte le même SQLSTATE que le refus définitif, mais il n'appelle pas la
 * même réaction : une fiche en corbeille peut encore être restaurée, donc le
 * rejeu redeviendra valide. On met en quarantaine — pas de rejeu en boucle —
 * mais SANS marquer l'échec définitif, pour que `rejouerQuarantaine()` reste
 * le geste naturel après une restauration.
 */
const MOTIF_CORBEILLE = /cliente en corbeille/i;

function estRefusCorbeille(erreur) {
  return codeErreur(erreur) === CODE_RECREATION_REFUSEE
    && MOTIF_CORBEILLE.test(String((erreur && erreur.message) || ''));
}

/** Le code PostgreSQL d'une erreur Supabase, sous forme de chaîne. */
export function codeErreur(erreur) {
  const c = erreur && (erreur.code ?? erreur.status);
  return c === undefined || c === null ? '' : String(c);
}

/**
 * Un rejeu peut-il, en principe, changer le résultat ?
 *
 * @param {object} erreur
 * @param {{ cibleDefinitivementSupprimee?: boolean }} contexte - un tombstone
 *   identifie-t-il la cible comme purgée volontairement ?
 */
export function estDefinitif(erreur, contexte = {}) {
  if (estRecreationRefusee(erreur)) return true;
  if (codeErreur(erreur) === CODE_CLE_ETRANGERE) {
    // Définitif SEULEMENT si un tombstone l'atteste. Sinon la cliente peut
    // encore arriver : sa propre opération est peut-être derrière dans la file.
    return contexte.cibleDefinitivementSupprimee === true;
  }
  return Object.prototype.hasOwnProperty.call(CODES_DEFINITIFS, codeErreur(erreur));
}

/**
 * L'échec vient-il du RÉSEAU plutôt que du serveur ?
 *
 * Distinction demandée par Anissa (2026-08-01) : sans elle, cinq démarrages
 * hors ligne consommeraient les cinq tentatives et mettraient en quarantaine
 * des données parfaitement valides. Une requête qui n'est jamais partie n'est
 * pas un refus du serveur, elle ne doit rien consommer.
 */
export function estEchecReseau(erreur) {
  if (!erreur) return false;
  if (codeErreur(erreur)) return false; // le serveur a répondu, donc joignable
  const m = String(erreur.message || erreur.name || '').toLowerCase();
  return /failed to fetch|networkerror|network request failed|load failed|typeerror|econnrefused|timeout|aborted/.test(m)
    || m === '';
}

/**
 * Que faire d'une opération qui vient d'échouer ?
 *
 * @param {{code?: string|number, message?: string}|null} erreur
 * @param {number} tentatives - tentatives DÉJÀ effectuées avant celle-ci
 * @returns {{ decision: string, motif: string, definitif: boolean }}
 */
export function classerEchec(erreur, tentatives = 0, contexte = {}) {
  const code = codeErreur(erreur);

  // Refus de recréation : code ET motif, pour ne pas capturer une autre
  // exception personnalisée qui porterait le même SQLSTATE.
  if (estRecreationRefusee(erreur)) {
    return {
      decision: DECISION_ECHEC.QUARANTAINE,
      motif: 'entité définitivement supprimée — recréation refusée (code P0002)',
      definitif: true,
      compteTentative: true,
    };
  }

  // Cliente en corbeille : refus réel, mais réversible par une restauration.
  if (estRefusCorbeille(erreur)) {
    return {
      decision: DECISION_ECHEC.QUARANTAINE,
      motif: 'cliente en corbeille — recréation refusée (code P0002), restaurable',
      definitif: false,
      compteTentative: true,
    };
  }

  // Clé étrangère : définitive uniquement si un tombstone l'atteste.
  if (code === CODE_CLE_ETRANGERE) {
    if (contexte.cibleDefinitivementSupprimee === true) {
      return {
        decision: DECISION_ECHEC.QUARANTAINE,
        motif: 'clé étrangère vers une entité définitivement supprimée (code 23503, tombstone confirmé)',
        definitif: true,
        compteTentative: true,
      };
    }
    // Aucun tombstone : la cible peut encore arriver. On attend.
    if (tentatives + 1 >= MAX_TENTATIVES) {
      return {
        decision: DECISION_ECHEC.QUARANTAINE,
        motif: `clé étrangère non résolue après ${MAX_TENTATIVES} tentatives (code 23503, aucun tombstone)`,
        definitif: false,
        compteTentative: true,
      };
    }
    return {
      decision: DECISION_ECHEC.REJOUER,
      motif: `clé étrangère absente, cible peut-être pas encore synchronisée (code 23503), tentative ${tentatives + 1}`,
      definitif: false,
      compteTentative: true,
    };
  }

  if (estDefinitif(erreur, contexte)) {
    return {
      decision: DECISION_ECHEC.QUARANTAINE,
      motif: `${CODES_DEFINITIFS[code]} (code ${code})`,
      definitif: true,
      compteTentative: true,
    };
  }

  // Panne réseau : la requête n'est jamais arrivée au serveur. On rejoue SANS
  // consommer de tentative — sinon une semaine hors ligne mettrait en
  // quarantaine des données parfaitement valides.
  if (estEchecReseau(erreur)) {
    return {
      decision: DECISION_ECHEC.REJOUER,
      motif: 'réseau indisponible — aucune tentative consommée',
      definitif: false,
      compteTentative: false,
    };
  }

  // Le serveur a répondu et refuse, sans code reconnu. Là, on compte.
  if (tentatives + 1 >= MAX_TENTATIVES) {
    return {
      decision: DECISION_ECHEC.QUARANTAINE,
      motif: `${MAX_TENTATIVES} tentatives sans succès${code ? ` (dernier code ${code})` : ''}`,
      definitif: false,
      compteTentative: true,
    };
  }

  return {
    decision: DECISION_ECHEC.REJOUER,
    motif: `échec passager${code ? ` (code ${code})` : ''}, tentative ${tentatives + 1}`,
    definitif: false,
    compteTentative: true,
  };
}

/**
 * Ajoute une opération à la file SANS créer de doublon.
 *
 * L'ancienne version empilait sans regarder : chaque sauvegarde d'une même
 * ligne ajoutait une copie. On remplace l'opération existante de même type et
 * même identifiant — c'est la dernière version qui compte — en conservant le
 * compteur de tentatives déjà accumulé.
 *
 * @returns {Array} une NOUVELLE file, l'entrée n'est pas mutée
 */
export function fusionnerDansFile(file, operation, maintenant = 0) {
  const liste = Array.isArray(file) ? file : [];
  const idNouveau = operation && operation.data && operation.data.id;
  const memeCible = (o) => o
    && o.type === operation.type
    && o.data && o.data.id !== undefined
    && o.data.id === idNouveau;

  const existante = idNouveau !== undefined ? liste.find(memeCible) : null;
  const entree = {
    ...operation,
    timestamp: maintenant,
    tentatives: existante ? (existante.tentatives || 0) : 0,
  };

  if (!existante) return [...liste, entree];
  return liste.map((o) => (o === existante ? entree : o));
}

/** Résumé lisible pour la praticienne. Ne contient aucune donnée métier. */
export function resumerQuarantaine(quarantaine) {
  const liste = Array.isArray(quarantaine) ? quarantaine : [];
  if (liste.length === 0) return '';
  const parMotif = {};
  for (const q of liste) {
    const m = q && q.motif ? q.motif : 'motif inconnu';
    parMotif[m] = (parMotif[m] || 0) + 1;
  }
  const details = Object.entries(parMotif)
    .map(([m, n]) => `${n} × ${m}`)
    .join(' ; ');
  return `${liste.length} élément${liste.length > 1 ? 's' : ''} en quarantaine — ${details}`;
}
